import { memo, useEffect, useState } from 'react';
import Image from 'next/image';
import { Box, Stack, Typography } from '@deltasierra/react-components-core';
import { AssetLibraryAssetTypeImage } from './AssetLibraryAssetTypeImage';
import { MimeType } from './constants';
import { isAllowedExtensionByUrl } from './utils';

type AssetLibraryGalleryGridItemThumbnailProps = {
    alt: string;
    height: number;
    mimeType?: MimeType;
    url?: string;
    width: number;
};

export const AssetLibraryGalleryGridItemThumbnail = memo(
    ({ alt, height, mimeType, url, width }: AssetLibraryGalleryGridItemThumbnailProps): JSX.Element => {
        const [isError, setIsError] = useState(false);

        useEffect(() => {
            setIsError(!url);
        }, [url]);

        return (
            <Box
                sx={{
                    alignItems: 'center',
                    borderRadius: 1,
                    color: 'primary.main',
                    display: 'flex',
                    height,
                    justifyContent: 'center',
                    overflow: 'hidden',
                    path: { strokeWidth: 1 },
                    width,
                }}
            >
                {!url || isError || !isAllowedExtensionByUrl(url) ? (
                    <Stack alignItems="center" justifyContent="center" padding={1} width="100%">
                        <AssetLibraryAssetTypeImage height={80} mimeType={mimeType} width={80} />
                        <Typography
                            sx={{
                                maxWidth: '80%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            variant="body1"
                        >
                            {alt}
                        </Typography>
                    </Stack>
                ) : (
                    <Image
                        alt={alt}
                        draggable={false}
                        height={height}
                        src={url}
                        style={{
                            borderRadius: 1,
                            height,
                            objectFit: 'contain',
                            overflow: 'hidden',
                            width,
                        }}
                        width={width}
                        onError={() => {
                            setIsError(true);
                        }}
                    />
                )}
            </Box>
        );
    },
    (prevProps, nextProps) =>
        prevProps.url === nextProps.url && prevProps.width === nextProps.width && prevProps.height === nextProps.height,
);

AssetLibraryGalleryGridItemThumbnail.displayName = 'AssetLibraryGalleryGridItemThumbnail';
